import { EmailTypeEnum, EntityTypeEnum } from '@api/ringil-api.v2';

type Translate = (key: string, defaultValue?: string) => string;

/**
 * Email types are a long, backend-owned enum (~90 values) and translations live in Locize, not here.
 * The fallback is derived from the code itself ("NEW_SPOT_DEMAND_V2" → "New spot demand") so an
 * untranslated key still renders something readable instead of a raw enum constant.
 */
export const humanizeEmailType = (code: string): string => {
  const words = code
    .replace(/_V\d+$/, '')
    .split('_')
    .filter(w => w.length > 0)
    .map(w => w.toLowerCase());
  if (words.length === 0) return code;
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
};

export const notificationTypeLabel = (tr: Translate, code: string): string => tr(`NotificationType.${code}`, humanizeEmailType(code));

/** Entity types notifications are actually raised for — the "Druh" filter and the row chip use these. */
export const NOTIFICATION_ENTITY_TYPES: EntityTypeEnum[] = [
  EntityTypeEnum.EXPEDITION,
  EntityTypeEnum.TIMESLOT,
  EntityTypeEnum.SUPPLIER_NOTIFICATION,
  EntityTypeEnum.SUPPLIER_MANIFEST,
  EntityTypeEnum.SHIPMENT,
  EntityTypeEnum.GROUP_DEMAND,
];

const ENTITY_TYPE_FALLBACK: Partial<Record<EntityTypeEnum, string>> = {
  [EntityTypeEnum.EXPEDITION]: 'Expedition',
  [EntityTypeEnum.TIMESLOT]: 'Reservation',
  [EntityTypeEnum.SUPPLIER_NOTIFICATION]: 'Supplier notification',
  [EntityTypeEnum.SUPPLIER_MANIFEST]: 'Supplier manifest',
  [EntityTypeEnum.SHIPMENT]: 'Shipment',
  [EntityTypeEnum.GROUP_DEMAND]: 'Group demand',
};

export const entityTypeLabel = (tr: Translate, entityType: EntityTypeEnum): string =>
  tr(`NotificationCenter.entityType.${entityType}`, ENTITY_TYPE_FALLBACK[entityType] ?? humanizeEmailType(entityType));

/** Message notifications open the entity straight on its messages tab, mirroring the legacy list. */
const MESSAGE_EMAIL_TYPES: string[] = [EmailTypeEnum.NEW_MESSAGE_V2, EmailTypeEnum.CU_NEW_MESSAGE, EmailTypeEnum.NEW_MESSAGE_SN, EmailTypeEnum.NEW_MESSAGE_GD];

export const isMessageNotification = (type?: string): boolean => !!type && MESSAGE_EMAIL_TYPES.includes(type);